import React, { useContext } from 'react';
import { isEmpty } from 'lodash';
import { FormattedMessage } from 'react-intl';

import {
  Checkbox,
  Heading,
  PositiveNumberField,
  TextStyle,
  Tippy
} from '@superops/ui-design-system';

import { ServiceCatalogFormContext } from '../index';
import { ServiceCatalogItem } from '../../types';
import { SVGIcon } from '@superops/super-icons';
import { Row, CurrencyField } from '@nile/components';

function ProductTypeItem({
  selectedCatalogItem,
  handlePropertyChange,
  disableServiceCatalogItemCostAndPrice
}: {
  selectedCatalogItem: ServiceCatalogItem;
  handlePropertyChange: (key, val) => void;
  disableServiceCatalogItemCostAndPrice: boolean;
}): React.ReactElement {
  const { errors, currencyValue, currencyCode } = useContext(
    ServiceCatalogFormContext
  );

  function handleChange(key) {
    switch (key) {
      case 'COST':
        return (value) =>
          handlePropertyChange(
            'costPrice',
            isEmpty(value)
              ? null
              : {
                  ...selectedCatalogItem?.costPrice,
                  model: 'PER_UNIT',
                  details: [
                    {
                      ...selectedCatalogItem?.costPrice?.details[0],
                      value: value
                    }
                  ]
                }
          );
      default:
        return (value) =>
          handlePropertyChange('sellingPrice', {
            ...selectedCatalogItem?.sellingPrice,
            model: 'PER_UNIT',
            details: [
              {
                ...selectedCatalogItem?.sellingPrice?.details[0],
                value: value || null
              }
            ]
          });
    }
  }

  return (
    <div className="grid g20">
      <CurrencyField
        type="number"
        min={0}
        label={
          <Row gap={5}>
            <Heading element="h3">
              <FormattedMessage id="admin.serviceCatalog.offeringType.quantity.cost" />
            </Heading>
            :{' '}
            <div className="self-center">
              <TextStyle variation="subdued">
                <FormattedMessage id="admin.serviceCatalog.offeringType.quantity.cost.description" />
              </TextStyle>
            </div>
          </Row>
        }
        value={selectedCatalogItem?.costPrice?.details[0]?.value}
        placeholder="0"
        // suffix={`${currencyValue}${currencyCode}`}
        autoComplete="off"
        onChange={handleChange('COST')}
        size="small"
        disabled={disableServiceCatalogItemCostAndPrice}
        showCurrency
        currencyInPrefix={false}
      />
      <CurrencyField
        type="number"
        label={
          <Row gap={5}>
            <Heading element="h3">
              <FormattedMessage id="admin.serviceCatalog.offeringType.quantity.price" />
            </Heading>
            :{' '}
            <div className="self-center">
              <TextStyle variation="subdued">
                <FormattedMessage id="admin.serviceCatalog.offeringType.quantity.price.description" />
              </TextStyle>
            </div>
          </Row>
        }
        size="small"
        placeholder="0"
        min={0}
        // suffix={`${currencyValue}${currencyCode}`}
        value={selectedCatalogItem?.sellingPrice?.details[0]?.value}
        autoComplete="off"
        onChange={handleChange('PRICE')}
        error={
          (errors?.['sellingPrice.details']?.[0] ||
            errors?.['sellingPrice']?.[0]) && (
            <FormattedMessage id="Price cannot be empty" />
          )
        }
        disabled={disableServiceCatalogItemCostAndPrice}
        showCurrency
        currencyInPrefix={false}
      />
      <div className="fx actr g10">
        <Checkbox
          label="Add this item to your tickets and projects"
          checked={selectedCatalogItem?.offerAsWorklogItem}
          onChange={(checked) => {
            handlePropertyChange('offerAsWorklogItem', checked);
          }}
          disabled={disableServiceCatalogItemCostAndPrice}
        />
        <Tippy
          title="Tick this box if you want to add this service item to your tickets/projects as a product or a worklog, and use it in invoices and quotes. You can ignore this if you just want to add it to contracts as a subscription item."
          theme="dark"
          trigger="mouseenter"
          delay={300}
          duration={200}
          className="flex-imp actr ptr"
        >
          <SVGIcon name="About" />
        </Tippy>
      </div>
    </div>
  );
}

export default ProductTypeItem;
